/**
 * SystemHealthCard — consolidated status for /api/admin/system-health.
 * Read-only. No secrets shown.
 */
import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Card } from "../ui/card";
import { Badge } from "../ui/badge";
import { RefreshCw, CheckCircle2, AlertTriangle, CircleDashed } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BACKEND}/api/admin/system-health`;

function authHeaders() {
  const token = localStorage.getItem("access_token") || localStorage.getItem("token") || "";
  return token ? { Authorization: `Bearer ${token}` } : {};
}

const STATE_STYLES = {
  healthy: { cls: "bg-green-100 text-green-800", label: "Sain", Icon: CheckCircle2 },
  configured: { cls: "bg-green-100 text-green-800", label: "Configuré", Icon: CheckCircle2 },
  degraded: { cls: "bg-amber-100 text-amber-800", label: "Dégradé", Icon: AlertTriangle },
  unavailable: { cls: "bg-red-100 text-red-800", label: "Indisponible", Icon: AlertTriangle },
  disabled: { cls: "bg-slate-100 text-slate-700", label: "Désactivé", Icon: CircleDashed },
  pending_activation: { cls: "bg-slate-100 text-slate-700", label: "En attente", Icon: CircleDashed },
  pending_first_checkpoint: { cls: "bg-slate-100 text-slate-700", label: "En attente", Icon: CircleDashed },
};

function StateBadge({ state }) {
  const s = STATE_STYLES[state] || { cls: "bg-slate-100 text-slate-700", label: state || "—", Icon: CircleDashed };
  const Icon = s.Icon;
  return (
    <Badge className={`${s.cls} flex items-center gap-1`} data-testid={`sh-badge-${state}`}>
      <Icon className="h-3 w-3" /> {s.label}
    </Badge>
  );
}

const COMPONENT_LABELS = {
  backend: "Backend API",
  mongodb: "MongoDB",
  cloudinary: "Cloudinary",
  resend: "Resend (email)",
  blue: "Blue (LLM)",
  updater: "Updater (127.0.0.1:9001)",
  backups: "Sauvegardes locales",
  disaster_recovery: "Reprise après sinistre",
};

function DetailLine({ children }) {
  return <p className="text-xs text-muted-foreground mt-1">{children}</p>;
}

function cloudinaryPctLabel(v) {
  return v == null ? "Limite non communiquée par Cloudinary" : `${v}%`;
}

function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("fr-FR"); } catch { return iso; }
}

function ComponentRow({ c }) {
  const label = COMPONENT_LABELS[c.component] || c.component;
  return (
    <div className="flex items-start justify-between gap-3 py-3 border-b last:border-b-0" data-testid={`sh-row-${c.component}`}>
      <div className="min-w-0 flex-1">
        <div className="text-sm font-medium text-primary">{label}</div>
        {c.component === "mongodb" && c.db_name && <DetailLine>Base : <code>{c.db_name}</code></DetailLine>}
        {c.component === "cloudinary" && c.configured && (
          <DetailLine>
            Crédits : {c.credits_pct ?? "—"}%
            {" · "}Stockage : {cloudinaryPctLabel(c.storage_pct)}
            {" · "}Bande : {cloudinaryPctLabel(c.bandwidth_pct)}
          </DetailLine>
        )}
        {c.component === "resend" && (
          <DetailLine>Mode test : {c.test_mode ? "oui" : "non"} · Whitelist : {c.whitelist_size}</DetailLine>
        )}
        {c.component === "blue" && (
          <DetailLine>Provider : {c.provider} · Modèle : {c.model} · Mode : {c.provider_mode}</DetailLine>
        )}
        {c.component === "updater" && (
          <DetailLine>Auto-update : {c.auto_update_enabled ? "activée" : "désactivée"}{c.detail ? ` · ${c.detail}` : ""}</DetailLine>
        )}
        {c.component === "backups" && (
          <>
            <DetailLine>
              Dernier checkpoint : {c.latest_checkpoint_id || "aucun"}
              {c.latest_checkpoint_type ? ` (${c.latest_checkpoint_type})` : ""}
              {c.latest_age_hours != null ? ` · âge ${c.latest_age_hours} h` : ""}
            </DetailLine>
            <DetailLine>
              Planificateur : {c.scheduler_enabled ? "activé" : "désactivé"}
              {c.next_run_at ? ` · prochaine ${fmtDate(c.next_run_at)}` : ""}
            </DetailLine>
            {c.last_scheduled_error && (
              <DetailLine><span className="text-red-700">Erreur planificateur : {c.last_scheduled_error}</span></DetailLine>
            )}
            {(c.open_alerts || []).length > 0 && (
              <DetailLine><span className="text-amber-700">{c.open_alerts.length} alerte(s) en cours</span></DetailLine>
            )}
          </>
        )}
        {c.component === "disaster_recovery" && (
          <>
            <DetailLine>
              Bootstrap : {c.bootstrap_prepared ? "prêt" : "manquant"}
              {c.dr_ready_checkpoint_id ? ` · dernier checkpoint DR : ${c.dr_ready_checkpoint_id}` : ""}
              {c.dr_format_version ? ` (v${c.dr_format_version})` : ""}
            </DetailLine>
            <DetailLine>Secrets : {c.secrets_strategy} · Modes : A + B</DetailLine>
          </>
        )}
      </div>
      <StateBadge state={c.state} />
    </div>
  );
}

export default function SystemHealthCard() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(API, { headers: authHeaders(), timeout: 8000 });
      setData(r.data);
      setError(null);
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || "Erreur inconnue");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <Card className="p-6" data-testid="system-health-card">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h3 className="font-heading text-lg font-semibold text-primary">État du système</h3>
          <p className="text-xs text-muted-foreground">
            {data ? `État global : ${data.overall}` : loading ? "Chargement…" : ""}
          </p>
        </div>
        <button onClick={load} className="text-xs text-primary underline flex items-center gap-1" data-testid="sh-refresh-btn">
          <RefreshCw className="h-3 w-3" /> Rafraîchir
        </button>
      </div>
      {error && <p className="text-xs text-red-700" data-testid="sh-error">{String(error)}</p>}
      {data && (
        <div className="mt-2">
          {data.components.map((c) => <ComponentRow key={c.component} c={c} />)}
        </div>
      )}
    </Card>
  );
}
